import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { SectionHeading } from './SectionHeading';
import { iconFor } from './iconMap';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1662622008881-ea6d94c98e9d?w=1100&q=80&fm=jpg&crop=entropy&cs=srgb';

export const Container = ({ children, className = '' }) => (
  <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

export const PrimaryCta = ({ to, children, testId, className = '' }) => (
  <Button asChild size="lg" className={`h-12 px-6 bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] active:scale-[0.98] transition-[background-color,transform] duration-200 font-semibold ${className}`} data-testid={testId}>
    <Link to={to}>{children} <ArrowRight className="w-4 h-4 ml-2" /></Link>
  </Button>
);

export const SecondaryCta = ({ to, children, testId, className = '' }) => (
  <Button asChild size="lg" variant="outline" className={`h-12 px-6 bg-[var(--lux-panel)] border-white/10 text-white hover:bg-[var(--lux-panel-2)] hover:border-lux-gold/40 transition-[background-color,border-color] duration-200 ${className}`} data-testid={testId}>
    <Link to={to}>{children}</Link>
  </Button>
);

/** Landing hero: editorial copy left, framed portrait + 5-axis card right. */
export const HeroSection = ({ copy, appHref, featuresHref }) => (
  <section className="relative overflow-hidden" aria-labelledby="hero-title">
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{ backgroundImage: 'radial-gradient(600px circle at 20% 10%, rgba(243,179,61,0.14), transparent 55%)' }} />
    <Container className="relative pt-14 pb-16 sm:pt-20 sm:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7 fade-in">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--lux-gold)] mb-4" data-testid="hero-eyebrow">{copy.eyebrow}</p>
        <h1 id="hero-title" className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white max-w-3xl" data-testid="hero-title">{copy.h1}</h1>
        <p className="mt-6 text-base sm:text-lg text-white/60 leading-relaxed max-w-2xl" data-testid="hero-subtitle">{copy.sub}</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <PrimaryCta to={appHref} testId="hero-primary-cta">{copy.ctaPrimary}</PrimaryCta>
          <SecondaryCta to={featuresHref} testId="hero-secondary-cta">{copy.ctaSecondary}</SecondaryCta>
        </div>
        <dl className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6" data-testid="hero-stats">
          {copy.stats.map(s => (
            <div key={s.label} className="border-l border-lux-gold/30 pl-4">
              <dt className="sr-only">{s.label}</dt>
              <dd className="font-display text-2xl text-white tabular-nums">{s.value}</dd>
              <dd className="text-xs text-white/50 mt-1">{s.label}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="lg:col-span-5 relative fade-in">
        <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_12px_32px_rgba(0,0,0,0.55)] aspect-[4/5] max-w-md mx-auto bg-[var(--lux-panel)]">
          <img src={HERO_IMAGE} alt={copy.heroAlt} width="880" height="1100" className="w-full h-full object-cover opacity-80" loading="eager" fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--lux-ink)] via-transparent to-transparent" aria-hidden="true" />
          <div className="absolute left-4 right-4 bottom-4 rounded-xl border border-lux-gold/25 bg-black/60 backdrop-blur-md p-4" data-testid="hero-axes-card">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--lux-amber)] mb-3">{copy.heroCard.label}</p>
            <ul className="grid grid-cols-1 gap-1.5">
              {copy.heroCard.axes.map((axis, i) => (
                <li key={axis} className="flex items-center justify-between text-sm text-white">
                  <span className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-lux-gold/15 text-[var(--lux-gold)] text-[10px] font-semibold flex items-center justify-center">{i + 1}</span>{axis}</span>
                  <span className="h-1 w-16 rounded-full bg-white/10 overflow-hidden" aria-hidden="true"><span className="block h-full bg-[var(--lux-gold)]" style={{ width: `${55 + i * 9}%` }} /></span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

export const StepsSection = ({ copy }) => (
  <section className="py-16 sm:py-24" aria-labelledby="steps-title">
    <Container>
      <SectionHeading title={copy.stepsTitle} sub={copy.stepsSub} testId="steps-heading" />
      <ol className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="steps-list">
        {copy.steps.map((step, i) => {
          const Icon = iconFor(step.icon);
          return (
            <li key={step.title} className="relative rounded-2xl border border-white/10 bg-lux-panel/90 p-6 hover:border-white/15 transition-[border-color] duration-200">
              <div className="flex items-center justify-between mb-5">
                <span className="w-11 h-11 rounded-xl bg-lux-gold/10 text-[var(--lux-gold)] flex items-center justify-center"><Icon className="w-5 h-5" /></span>
                <span className="font-display text-3xl text-white/15 tabular-nums">0{i + 1}</span>
              </div>
              <h3 className="text-lg font-semibold text-white">{step.title}</h3>
              <p className="mt-2 text-sm text-white/55 leading-relaxed">{step.text}</p>
            </li>
          );
        })}
      </ol>
    </Container>
  </section>
);

/** Feature cards. `detailed` adds the long copy + bullet list (features page). */
export const FeatureGrid = ({ features, lang, detailed = false, testId = 'feature-grid' }) => (
  <div className={`grid grid-cols-1 ${detailed ? 'md:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'} gap-6`} data-testid={testId}>
    {features.map(f => {
      const Icon = iconFor(f.icon);
      const c = f[lang];
      return (
        <Card key={f.key} id={f.key} className="bg-lux-panel/90 border-white/10 hover:border-white/15 transition-[border-color] duration-200 scroll-mt-24" data-testid={`feature-card-${f.key}`}>
          <CardContent className="p-6 space-y-4">
            <span className="w-11 h-11 rounded-xl bg-lux-gold/10 text-[var(--lux-gold)] flex items-center justify-center"><Icon className="w-5 h-5" /></span>
            <h3 className="text-lg font-semibold text-white">{c.title}</h3>
            <p className="text-sm text-white/55 leading-relaxed">{detailed ? c.long : c.short}</p>
            {detailed && (
              <ul className="space-y-2 pt-2">
                {c.bullets.map(b => (
                  <li key={b} className="flex items-start gap-2 text-sm text-white/75"><Check className="w-4 h-4 text-[var(--lux-gold)] mt-0.5 shrink-0" /> <span>{b}</span></li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      );
    })}
  </div>
);

export const TrustSection = ({ copy }) => (
  <section className="py-16 sm:py-24 border-t border-white/[0.06]" aria-labelledby="trust-title">
    <Container className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
      <div className="lg:col-span-4">
        <SectionHeading title={copy.trustTitle} sub={copy.trustSub} testId="trust-heading" />
      </div>
      <ul className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-6" data-testid="trust-list">
        {copy.trust.map(item => {
          const Icon = iconFor(item.icon);
          return (
            <li key={item.title} className="rounded-2xl border border-lux-gold/20 bg-lux-gold/5 p-6">
              <Icon className="w-6 h-6 text-[var(--lux-gold)]" />
              <h3 className="mt-4 text-base font-semibold text-white">{item.title}</h3>
              <p className="mt-2 text-sm text-white/55 leading-relaxed">{item.text}</p>
            </li>
          );
        })}
      </ul>
    </Container>
  </section>
);

export const FaqAccordion = ({ faqs, testId = 'faq-accordion' }) => (
  <Accordion type="single" collapsible className="w-full divide-y divide-white/[0.08] rounded-2xl border border-white/10 bg-lux-panel/90 px-2 sm:px-4" data-testid={testId}>
    {faqs.map((f, i) => (
      <AccordionItem key={f.q} value={`faq-${i}`} className="border-b-0">
        <AccordionTrigger className="text-left text-base text-white hover:text-[var(--lux-gold)] hover:no-underline py-5" data-testid={`faq-trigger-${i}`}>
          <h3 className="font-medium pr-4">{f.q}</h3>
        </AccordionTrigger>
        <AccordionContent className="text-sm text-white/60 leading-relaxed pb-5 max-w-[68ch]" data-testid={`faq-content-${i}`}>{f.a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export const CtaBand = ({ title, sub, ctaLabel, to, testId = 'final-cta' }) => (
  <section className="py-16 sm:py-20" aria-label={title}>
    <Container>
      <div className="relative overflow-hidden rounded-3xl border border-lux-gold/25 bg-[var(--lux-panel)] px-6 py-12 sm:px-12 sm:py-16 text-center">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{ backgroundImage: 'radial-gradient(500px circle at 50% 0%, rgba(243,179,61,0.16), transparent 60%)' }} />
        <div className="relative">
          <h2 className="font-display text-3xl sm:text-4xl text-white">{title}</h2>
          <p className="mt-4 text-white/60 max-w-xl mx-auto leading-relaxed">{sub}</p>
          <div className="mt-8 flex justify-center">
            <PrimaryCta to={to} testId={testId}>{ctaLabel}</PrimaryCta>
          </div>
        </div>
      </div>
    </Container>
  </section>
);
